import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Chip,
  Stack,
  Card,
  CardContent,
  IconButton,
  Menu,
  Tabs,
  Tab,
  Autocomplete,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Event as EventIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import { useAuth } from '../../context/AuthContext';
import { useActiveMemberProfiles } from '../../hooks/useActiveMemberProfiles';
import { eventAPI, memberProfileAPI } from '../../services/api';
import { Event, EventStatus, EventRegistration, EventRegistrationsResponse, MemberProfile, RegistrationStatus } from '../../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`event-tabpanel-${index}`}
      aria-labelledby={`event-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const EventDetailsPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState<Event | null>(null);
  const [registrations, setRegistrations] = useState<EventRegistrationsResponse>({
    registered: [],
    waitlisted: [],
    totalRegistered: 0,
    totalWaitlisted: 0
  });
  
  // Fetch all member profiles (not filtered by active status)
  const { profiles: memberProfiles, refetch: refetchMemberProfiles } = useActiveMemberProfiles({ activeOnly: false, sortByName: false });
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState<boolean>(false);
  const [selectedMemberProfile, setSelectedMemberProfile] = useState<MemberProfile | null>(null);
  const [selectedRegistration, setSelectedRegistration] = useState<EventRegistration | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [adminGuests, setAdminGuests] = useState<{ name: string; email: string; phone: string }[]>([]);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRegistration, setMenuRegistration] = useState<EventRegistration | null>(null);

  // Fetch event details
  const fetchEvent = async () => {
    if (!eventId) return;
    
    try {
      const response = await eventAPI.getById(eventId);
      setEvent(response.data);
    } catch (error) {
      console.error('Error fetching event:', error);
      setError('Failed to load event details');
    }
  };

  // Fetch event registrations
  const fetchRegistrations = async () => {
    if (!eventId) return;
    
    try {
      const response = await eventAPI.getEventRegistrations(eventId);
      setRegistrations(response.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      setError('Failed to load registrations');
    }
  };

  // Fetch member profiles for adding users
  // Initial data loading
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchEvent(),
        fetchRegistrations(),
        refetchMemberProfiles()
      ]);
      setLoading(false);
    };

    loadData();
  }, [eventId]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle menu actions
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, registration: EventRegistration) => {
    setAnchorEl(event.currentTarget);
    setMenuRegistration(registration);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRegistration(null);
  };

  // Handle add user dialog
  const handleOpenAddDialog = () => {
    setSelectedMemberProfile(null);
    setDialogError(null);
    setAdminGuests([]);
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setSelectedMemberProfile(null);
    setDialogError(null);
    setAdminGuests([]);
  };

  // Handle remove user dialog
  const handleOpenRemoveDialog = (registration: EventRegistration) => {
    setSelectedRegistration(registration);
    setDialogError(null);
    setRemoveDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseRemoveDialog = () => {
    setRemoveDialogOpen(false);
    setSelectedRegistration(null);
    setDialogError(null);
  };

  // Add user to event
  const handleAddUser = async () => {
    if (!selectedMemberProfile || !eventId) {
      setDialogError('Please select a member to add');
      return;
    }

    // Check if user is already registered
    const alreadyRegistered = [
      ...registrations.registered,
      ...registrations.waitlisted
    ].some(reg => reg.memberProfileId === selectedMemberProfile.id);

    if (alreadyRegistered) {
      setDialogError('This member is already registered for this event');
      return;
    }

    setSubmitting(true);
    try {
      await eventAPI.register(eventId, {
        memberProfileId: selectedMemberProfile.id,
        notes: `Added by admin: ${user?.email}`,
        guests: adminGuests.length > 0 ? adminGuests : undefined
      });
      
      setSuccess(`Successfully registered ${selectedMemberProfile.firstName} ${selectedMemberProfile.lastName}`);
      handleCloseAddDialog();
      await fetchRegistrations();
      await fetchEvent(); // Refresh event to get updated counts
    } catch (error: any) {
      setDialogError(error.response?.data?.message || 'Failed to register user');
    } finally {
      setSubmitting(false);
    }
  };

  // Remove user from event
  const handleRemoveUser = async () => {
    if (!selectedRegistration) return;

    setSubmitting(true);
    try {
      await eventAPI.cancelRegistration(
        selectedRegistration.id,
        `Removed by admin: ${user?.email}`
      );
      
      setSuccess(`Successfully removed ${selectedRegistration.memberName}`);
      handleCloseRemoveDialog();
      await fetchRegistrations();
      await fetchEvent(); // Refresh event to get updated counts
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to remove registration');
      handleCloseRemoveDialog();
    } finally {
      setSubmitting(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Get status color
  const getStatusColor = (status: EventStatus) => {
    switch (status) {
      case EventStatus.PUBLISHED:
        return 'success';
      case EventStatus.DRAFT:
        return 'default';
      case EventStatus.CANCELLED:
        return 'error';
      case EventStatus.COMPLETED:
        return 'info';
      default:
        return 'default';
    }
  };

  // Get registration status color
  const getRegistrationStatusColor = (status: RegistrationStatus) => {
    switch (status) {
      case RegistrationStatus.REGISTERED:
        return 'success';
      case RegistrationStatus.WAITLISTED:
        return 'warning';
      case RegistrationStatus.CANCELLED:
        return 'error';
      case RegistrationStatus.ATTENDED:
        return 'info';
      case RegistrationStatus.NO_SHOW:
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Event not found</Alert>
      </Container>
    );
  }

  // Filter available member profiles (exclude already registered)
  const availableMemberProfiles = memberProfiles.filter(profile => 
    ![...registrations.registered, ...registrations.waitlisted]
      .some(reg => reg.memberProfileId === profile.id)
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <RouterLink to="/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </RouterLink>
        <RouterLink to="/event-management" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <EventIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Event Management
        </RouterLink>
        <Typography color="text.primary">
          {event.title}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/event-management')}
                sx={{ mr: 2 }}
              >
                Back to Events
              </Button>
              <Chip
                label={event.status.replace('_', ' ').toUpperCase()}
                color={getStatusColor(event.status) as any}
                size="small"
              />
            </Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {event.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {event.description}
            </Typography>
            {event.category && (
              <Chip label={event.category} variant="outlined" sx={{ mb: 2 }} />
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenAddDialog}
            disabled={event.status === EventStatus.CANCELLED}
          >
            Add Participant
          </Button>
        </Box>

        {/* Event Details Grid */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle2" color="primary">
                    Date & Time
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {formatDate(event.eventDateTime)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle2" color="primary">
                    Location
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {event.location || 'Not specified'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoneyIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle2" color="primary">
                    Cost
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="bold">
                  {event.cost > 0 ? formatCurrency(event.cost) : 'Free'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle2" color="primary">
                    Capacity
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {registrations.totalRegistered}
                  {event.maxRegistrants && ` / ${event.maxRegistrants}`}
                  {!event.maxRegistrants && ' registered'}
                </Typography>
                {registrations.totalWaitlisted > 0 && (
                  <Typography variant="caption" color="warning.main">
                    {registrations.totalWaitlisted} waitlisted
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {event.notes && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Additional Notes:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {event.notes}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Registrations Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="event registrations tabs">
            <Tab 
              label={`Registered (${registrations.totalRegistered})`} 
              id="event-tab-0"
              aria-controls="event-tabpanel-0"
            />
            <Tab 
              label={`Waitlist (${registrations.totalWaitlisted})`} 
              id="event-tab-1"
              aria-controls="event-tabpanel-1"
            />
          </Tabs>
        </Box>

        {/* Registered Users Tab */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Member</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Registration Date</TableCell>
                  <TableCell>Amount Paid</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registrations.registered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No registered participants yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  registrations.registered.map((registration) => (
                    <TableRow key={registration.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {registration.memberName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {registration.memberEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(registration.registeredAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatCurrency(registration.amountPaid)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={registration.status.replace('_', ' ').toUpperCase()}
                          color={getRegistrationStatusColor(registration.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => handleMenuClick(e, registration)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Waitlisted Users Tab */}
        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Member</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Waitlist Position</TableCell>
                  <TableCell>Registration Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registrations.waitlisted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No waitlisted participants
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  registrations.waitlisted.map((registration) => (
                    <TableRow key={registration.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {registration.memberName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {registration.memberEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {registration.waitlistPosition || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(registration.registeredAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={registration.status.replace('_', ' ').toUpperCase()}
                          color={getRegistrationStatusColor(registration.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => handleMenuClick(e, registration)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem 
          onClick={() => menuRegistration && handleOpenRemoveDialog(menuRegistration)}
          sx={{ color: 'error.main' }}
        >
          <PersonRemoveIcon sx={{ mr: 1 }} />
          Remove Registration
        </MenuItem>
      </Menu>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Participant to Event</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          
          <Autocomplete
            options={availableMemberProfiles}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
            getOptionKey={(option) => option.id}
            value={selectedMemberProfile}
            onChange={(event, newValue) => setSelectedMemberProfile(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Member"
                placeholder="Search by name or email..."
                fullWidth
                margin="normal"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Box>
                    <Typography variant="subtitle2">
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Box>
                </li>
              );
            }}
          />

          {/* Guest Registration Section */}
          {event?.guestsAllowed && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Guest Registration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {event.guestFee && event.guestFee > 0
                  ? `Guest fee: ${formatCurrency(event.guestFee)} per guest`
                  : 'Guests can attend for free'}
                {event.maxGuestsPerMember &&
                  ` • Max ${event.maxGuestsPerMember} guest(s) per member`}
              </Typography>

              {adminGuests.map((guest, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Guest {index + 1}</Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setAdminGuests(adminGuests.filter((_, i) => i !== index))}
                    >
                      Remove
                    </Button>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Guest Name"
                        required
                        value={guest.name}
                        onChange={(e) => {
                          const updated = [...adminGuests];
                          updated[index] = { ...updated[index], name: e.target.value };
                          setAdminGuests(updated);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Guest Email"
                        type="email"
                        required
                        value={guest.email}
                        onChange={(e) => {
                          const updated = [...adminGuests];
                          updated[index] = { ...updated[index], email: e.target.value };
                          setAdminGuests(updated);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Guest Phone"
                        type="tel"
                        value={guest.phone}
                        onChange={(e) => {
                          const updated = [...adminGuests];
                          updated[index] = { ...updated[index], phone: e.target.value };
                          setAdminGuests(updated);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}

              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  if (event.maxGuestsPerMember && adminGuests.length >= event.maxGuestsPerMember) {
                    setDialogError(`Maximum ${event.maxGuestsPerMember} guest(s) allowed per member`);
                    return;
                  }
                  setAdminGuests([...adminGuests, { name: '', email: '', phone: '' }]);
                }}
                disabled={
                  event.maxGuestsPerMember
                    ? adminGuests.length >= event.maxGuestsPerMember
                    : false
                }
              >
                Add Guest
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button 
            onClick={handleAddUser}
            variant="contained"
            disabled={!selectedMemberProfile || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Add Participant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove User Confirmation Dialog */}
      <Dialog open={removeDialogOpen} onClose={handleCloseRemoveDialog}>
        <DialogTitle>Remove Participant</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to remove {selectedRegistration?.memberName} from this event?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemoveDialog}>Cancel</Button>
          <Button 
            onClick={handleRemoveUser}
            color="error"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventDetailsPage;
